import React, { forwardRef, useCallback } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ReactComponent as CheckIcon } from 'src/assets/success_icon.svg';
import { ReactComponent as CloseIcon } from 'src/assets/cross_icon.svg';
import { useNotificationContext } from 'react-admin';
import { palette } from 'src/Theme';

interface ShsSuccessNotificationProps {
    message: string;
}

const ShsSuccessNotification = forwardRef<HTMLDivElement, ShsSuccessNotificationProps>(({ message }, ref) => {
    const { addNotification } = useNotificationContext();

    const handleClose = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            // In react-admin v4, adding a new notification to the queue
            // triggers the 'setOpen(false)' logic for the current notification.
            // By adding a dummy notification with a 1ms duration, we effectively
            // dismiss the current one immediately.
            addNotification({
                message: ' ',
                type: 'info',
                notificationOptions: { autoHideDuration: 1 }
            });
        },
        [addNotification]
    );

    return (
        <Box
            ref={ref}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                backgroundColor: palette.success.main,
                color: palette.success.darkGreenText,
                padding: '12px 24px',
                borderRadius: '12px',
                width: '100%',
                boxSizing: 'border-box',
                position: 'relative',
                pr: '48px',
                pointerEvents: 'auto' // Ensure the container and its children can receive clicks
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: palette.success.green,
                    flexShrink: 0
                }}>
                <CheckIcon />
            </Box>
            <Typography
                sx={{
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: '1.2'
                }}>
                {message}
            </Typography>
            <IconButton
                onClick={handleClose}
                size="medium"
                sx={{
                    position: 'absolute',
                    top: '0px',
                    right: '0px',
                    zIndex: 10
                }}>
                <CloseIcon />
            </IconButton>
        </Box>
    );
});

ShsSuccessNotification.displayName = 'ShsSuccessNotification';

export default ShsSuccessNotification;
